import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Quote,
  ChevronLeft,
  ChevronRight,
  PlaneTakeoff,
  Luggage,
  HeartHandshake,
  Activity,
  ShieldCheck,
  CalendarClock,
} from "lucide-react";
import { TESTIMONIALS } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import { useQuote } from "./QuoteModal";

export const SERVICE_ICONS = {
  PlaneTakeoff,
  Luggage,
  HeartHandshake,
  Activity,
  ShieldCheck,
};

export function ScheduleCallButton({
  className = "btn-primary",
  testId,
  children = "Schedule a Call",
}) {
  const { openQuote } = useQuote();

  return (
    <button
      type="button"
      onClick={() => openQuote()}
      className={`${className} group`}
      data-testid={testId}
    >
      <CalendarClock className="h-4 w-4 shrink-0" />
      {children}
    </button>
  );
}

export function PillBadge({ children, light = false }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.14em] ${
        light
          ? "bg-white/15 text-[#D2EBF8]"
          : "bg-[#E2F2FA] text-[#006FB9]"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          light ? "bg-[#B9DFF3]" : "bg-[#006FB9]"
        }`}
      />
      {children}
    </span>
  );
}

export function SectionHeading({
  badge,
  title,
  subtitle,
  center = true,
  light = false,
}) {
  return (
    <div
      className={`${center ? "mx-auto text-center" : ""} max-w-2xl`}
      data-reveal
    >
      {badge && <PillBadge light={light}>{badge}</PillBadge>}

      <h2
        className={`mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight sm:text-4xl lg:text-[44px] ${
          light ? "text-white" : "text-[#0B2748]"
        }`}
      >
        {title}
      </h2>

      {subtitle && (
        <p
          className={`mt-4 text-[15.5px] leading-relaxed ${
            light ? "text-[#C5DEEC]" : "text-[#526A7D]"
          }`}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

// Inner-page hero banner
export function PageHero({
  badge,
  title,
  subtitle,
  image,
  imageAlt = "",
}) {
  return (
    <section className="page-hero relative isolate min-h-[430px] overflow-hidden bg-[#073B67] lg:min-h-[510px]">
      {image && (
        <div className="page-hero-media absolute inset-0">
          <img
            src={image}
            alt={imageAlt}
            className="h-full w-full object-cover object-center"
          />
        </div>
      )}

      <div className="pointer-events-none absolute inset-0 bg-[#073B67]/70" />

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#052F54]/80 via-[#073B67]/35 to-[#073B67]/10" />

      <div className="pointer-events-none absolute -left-28 -top-28 h-80 w-80 rounded-full bg-[#0785C7]/20 blur-3xl" />

      <div className="relative mx-auto flex min-h-[430px] max-w-[1440px] items-center lg:min-h-[510px]">
        <div className="relative z-10 px-6 py-16 sm:py-20 lg:px-12 lg:py-24 xl:pl-20">
          <div className="hero-stagger max-w-2xl">
            <PillBadge light>{badge}</PillBadge>

            <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-[56px]">
              {title}
            </h1>

            {subtitle && (
              <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-[#D7EAF5] sm:text-[17px]">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function ServiceCard({
  service,
  index = 0,
  image = service.image,
}) {
  const Icon = SERVICE_ICONS[service.icon] || ShieldCheck;

  return (
    <Link
      to={`/services/${service.slug}`}
      data-reveal
      style={{ transitionDelay: `${index * 80}ms` }}
      data-testid={`service-card-${service.slug}`}
      className="service-card tilt-card group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:shadow-[0_24px_48px_rgba(0,111,185,0.16)]"
    >
      <div className="relative h-52">
        <div className="h-full overflow-hidden rounded-t-2xl">
          <img
            src={image}
            alt={`${service.title} coverage`}
            loading="lazy"
            className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-[#073B67]/50 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        </div>

        <span className="absolute -bottom-6 right-6 grid h-14 w-14 place-items-center rounded-2xl bg-[#006FB9] shadow-lg transition-all duration-500 group-hover:rotate-6 group-hover:bg-[#B9DFF3]">
          <Icon className="h-6 w-6 text-[#B9DFF3] transition-colors duration-500 group-hover:text-[#006FB9]" />
        </span>
      </div>

      <div className="flex flex-1 flex-col p-7 pt-9">
        <h3 className="font-heading text-xl font-bold text-[#0B2748]">
          {service.title}
        </h3>

        <p className="mt-3 flex-1 text-sm leading-relaxed text-[#526A7D]">
          {service.short}
        </p>

        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#006FB9]">
          Learn More
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}

// Shared testimonial design for all pages.
// Automatically changes every 5 seconds: 1 → 2 → 3 → 1.
export function TestimonialsSection({
  testimonials = TESTIMONIALS,
  subtitle = "Real words from the families and professionals we've protected across British Columbia.",
  className = "",
}) {
  const [active, setActive] = useState(0);

  const ref = useRevealGroup();
  const count = testimonials.length;
  const currentIndex = count > 0 ? active % count : 0;

  useEffect(() => {
    if (count < 2) return undefined;

    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % count);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [count]);

  const changeSlide = (index) => {
    if (count < 2) return;
    setActive((index + count) % count);
  };

  if (!count) return null;

  return (
    <section
      ref={ref}
      className={`testimonials-section testimonials-redesigned ${className}`}
      aria-label="Client testimonials"
      aria-roledescription="carousel"
    >
      <div className="testimonials-layout">
        {/* Heading and navigation */}
        <div className="testimonials-intro" data-reveal>
          <PillBadge>Testimonials</PillBadge>

          <h2 className="testimonials-heading">
            Secured Lives, <span>Happy Clients</span>
          </h2>

          {subtitle && (
            <p className="testimonials-description">
              {subtitle}
            </p>
          )}

          {count > 1 && (
            <div className="testimonials-navigation">
              <div className="testimonials-controls">
                <button
                  type="button"
                  aria-label="Previous testimonial"
                  onClick={() => changeSlide(currentIndex - 1)}
                  className="testimonials-arrow"
                  data-testid="testimonial-prev"
                >
                  <ChevronLeft
                    aria-hidden="true"
                    className="h-5 w-5"
                  />
                </button>

                <span
                  className="testimonials-count"
                  aria-label={`Testimonial ${currentIndex + 1} of ${count}`}
                >
                  <strong>
                    {String(currentIndex + 1).padStart(2, "0")}
                  </strong>

                  <span aria-hidden="true">/</span>

                  {String(count).padStart(2, "0")}
                </span>

                <button
                  type="button"
                  aria-label="Next testimonial"
                  onClick={() => changeSlide(currentIndex + 1)}
                  className="testimonials-arrow"
                  data-testid="testimonial-next"
                >
                  <ChevronRight
                    aria-hidden="true"
                    className="h-5 w-5"
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Reviews share one grid cell to prevent height jumps */}
        <div className="testimonials-review-area" data-reveal>
          <Quote
            aria-hidden="true"
            className="testimonials-quote-mark"
          />

          <div
            className="testimonials-slides"
            aria-live="off"
          >
            {testimonials.map((item, index) => {
              const isActive = index === currentIndex;

              return (
                <figure
                  key={`${item.name}-${index}`}
                  className={`testimonials-slide ${
                    isActive ? "is-active" : ""
                  }`}
                  aria-hidden={!isActive}
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${count}`}
                >
                  <blockquote className="testimonials-review-text">
                    <p>“{item.text}”</p>
                  </blockquote>

                  <figcaption className="testimonials-author">
                    <img
                      src={item.image}
                      alt={item.name}
                      loading="lazy"
                      className="testimonials-author-image"
                    />

                    <div>
                      <p className="testimonials-author-name">
                        {item.name}
                      </p>

                      <p className="testimonials-author-role">
                        {item.role}
                      </p>
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>

          {count > 1 && (
            <div
              className="testimonials-pagination"
              aria-label="Choose a testimonial"
            >
              {testimonials.map((item, index) => (
                <button
                  key={`${item.name}-dot-${index}`}
                  type="button"
                  onClick={() => changeSlide(index)}
                  aria-label={`Show testimonial ${index + 1}`}
                  aria-current={
                    index === currentIndex ? "true" : undefined
                  }
                  className={`testimonials-dot ${
                    index === currentIndex ? "is-active" : ""
                  }`}
                >
                  <span />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function CTASection({
  title = "Secure What Matters Most!",
  subtitle,
}) {
  const { openQuote } = useQuote();
  const ref = useRevealGroup();

  return (
    <section
      ref={ref}
      className="desktop-cta-section mobile-cta-section"
    >
      <div className="cta-card relative mx-auto max-w-7xl overflow-hidden">
        <div
          className="cta-orb cta-orb-top"
          aria-hidden="true"
        />

        <div
          className="cta-orb cta-orb-bottom"
          aria-hidden="true"
        />

        <ShieldCheck
          className="cta-watermark"
          aria-hidden="true"
        />

        <div className="desktop-cta-content relative flex flex-col items-center gap-9 px-6 py-14 text-center lg:flex-row lg:justify-between lg:px-16 lg:py-16 lg:text-left">
          <div className="desktop-cta-copy" data-reveal>
            <span className="desktop-cta-eyebrow">
              Personal advice. Lasting confidence.
            </span>

            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-[42px]">
              {title}
            </h2>

            <p className="mt-3 max-w-xl text-[15.5px] leading-relaxed text-[#C5DEEC]">
              {subtitle ||
                "Free consultations 7 days a week. No hidden fees. Your satisfaction is 100% guaranteed."}
            </p>
          </div>

          <div
            className="mobile-cta-actions desktop-cta-actions flex flex-col items-center gap-4 sm:flex-row"
            data-reveal
          >
            <button
              type="button"
              onClick={() => openQuote()}
              className="btn-light"
              data-testid="cta-get-quote"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>

            <ScheduleCallButton
              className="btn-light"
              testId="cta-call"
            />
          </div>
        </div>
      </div>
    </section>
  );
}